import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Dialog — a modal for focused content that is not a confirmation (ADR-0088
 * D2): Admin's expanded workflow diagram, Student's privacy notice, identity
 * check and end-of-call question. A confirmation of a destructive act is an
 * `AlertDialog`.
 *
 * Built on Base UI (ADR-0088 D3), replacing Admin's Radix dialog and the
 * dialogs Student composed by hand. Base UI traps focus, closes on Escape
 * and on a click outside, hides the page from assistive technology, labels
 * the dialog by its title and description, and returns focus to whatever
 * had it before, also when the dialog opened without a trigger.
 *
 * The size carries the shape (see `overlay-shape.ts`): `default` is Admin's
 * dense dialog, closed by the X in its corner and laid out by `DialogHeader`;
 * `lg` is Student's dialog card, closed by its own buttons. A customer
 * dialog's title and text take the page's type, as the rest of its card
 * does.
 *
 * Triggers and closes accept `asChild` (see `as-child.ts`) as well as Base
 * UI's `render`.
 */
import { RefreshBlocker } from './app-update-hooks.js';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Icon } from './icon.js';
import { createContext, forwardRef, useContext } from 'react';
import { asChildProps } from './as-child.js';
import { cn } from './cn.js';
import { cornerClose, modalMotion, modalPosition, modalSurface, overlayBackdrop, } from './overlay-shape.js';
export const Dialog = DialogPrimitive.Root;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogTrigger = forwardRef(({ asChild, children, ...props }, ref) => (_jsx(DialogPrimitive.Trigger, { ref: ref, "data-slot": "dialog-trigger", ...props, ...asChildProps({ asChild, children }) })));
DialogTrigger.displayName = 'DialogTrigger';
export const DialogClose = forwardRef(({ asChild, children, ...props }, ref) => (_jsx(DialogPrimitive.Close, { ref: ref, "data-slot": "dialog-close", ...props, ...asChildProps({ asChild, children }) })));
DialogClose.displayName = 'DialogClose';
/** The scrim. `DialogContent` renders it; export only for a dialog laid out by hand. */
export const DialogOverlay = forwardRef(({ className, ...props }, ref) => (_jsx(DialogPrimitive.Backdrop, { ref: ref, "data-slot": "dialog-overlay", className: cn(overlayBackdrop, className), ...props })));
DialogOverlay.displayName = 'DialogOverlay';
/**
 * The dialog box itself, for a dialog laid out by hand: Student's end-of-call
 * question positions it in the visible part of a pinch-zoomed page. Use
 * `DialogContent` otherwise.
 */
export const DialogPopup = forwardRef(({ className, ...props }, ref) => (_jsx(DialogPrimitive.Popup, { ref: ref, "data-slot": "dialog-content", className: cn('outline-none', className), ...props })));
DialogPopup.displayName = 'DialogPopup';
const DialogSizeContext = createContext('default');
export const DialogContent = forwardRef(({ className, children, size = 'default', showCloseButton = size === 'default', closeLabel = 'Close dialog', ...props }, ref) => (_jsxs(DialogPrimitive.Portal, { children: [_jsx(DialogOverlay, {}), _jsxs(DialogPrimitive.Popup, { ref: ref, "data-slot": "dialog-content", "data-size": size, className: cn(modalPosition, modalMotion, modalSurface[size], size === 'default' && 'flex max-h-[calc(100vh-2rem)] max-w-6xl flex-col overflow-hidden', className), ...props, children: [_jsx(RefreshBlocker, {}), _jsx(DialogSizeContext.Provider, { value: size, children: children }), showCloseButton && (_jsx(DialogPrimitive.Close, { "aria-label": closeLabel, className: cornerClose, children: _jsx(Icon, { name: "close" }) }))] })] })));
DialogContent.displayName = 'DialogContent';
/** A dense dialog's title bar, leaving room for the X. */
export function DialogHeader({ className, ...props }) {
    return _jsx("div", { className: cn('border-b border-border-default px-6 py-5 pr-14', className), ...props });
}
export const DialogTitle = forwardRef(({ className, ...props }, ref) => {
    const size = useContext(DialogSizeContext);
    return (_jsx(DialogPrimitive.Title, { ref: ref, className: cn(size === 'default' && 'font-display text-section-title font-semibold', className), ...props }));
});
DialogTitle.displayName = 'DialogTitle';
export const DialogDescription = forwardRef(({ className, ...props }, ref) => {
    const size = useContext(DialogSizeContext);
    return (_jsx(DialogPrimitive.Description, { ref: ref, className: cn(size === 'default' && 'mt-1 text-body text-text-secondary', className), ...props }));
});
DialogDescription.displayName = 'DialogDescription';
//# sourceMappingURL=dialog.js.map